import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Table,
  TableHead,
  TableContainer,
  Paper,
  TableCell,
  TableRow,
  TableBody,
  Button,
  TablePagination,
  Chip,
  Stack,
  Box,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Tooltip,
  Skeleton,
  Typography,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  CircularProgress,
  Modal,
  Fade,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';
import { PAIDTYPE_STATUS, PAID_STATUS } from 'utils/constants';

import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import * as actions from '../../redux/actions';
import ExpensesDetail from '../../views/Finance/ExpensesDetail';
import EditExpenses from '../../views/Finance/EditExpenses';

export default function ExpensesTableApplove({
  expenses,
  page,
  size,
  setPage,
  setSize,
  handleEdit,
  handleDelete,
  filter,
  handlePreApprove,
  handleApprove,
  handleApprove1,
  handleApprove2,
  handleApprove3,
  handleSuccess,
  handleBillPickup,
  handleOpenPay,
  handleReject,
  handleReject1,
  handleReject2,
  handleReject3,
  handleHold,
  handleReset,
  handleOpenPrepareModal,
  handlePrepareStatus,
  handleEditLaborDraft,
  onResetLabor,
  onRowDoubleClick,
  onDoubleClickProject,
  onDetailClose,
  me,
  info,
  // filter สถานะแบบเลือกได้หลาย chip พร้อมกัน (multi-select) — statusFilters เป็น array ของ key ที่กำลัง
  // เลือกอยู่ (ว่าง = "ทั้งหมด" ไม่กรอง) onStatusFilterToggle(key) toggle key นั้นเข้า/ออกจาก array
  // statusOptions เป็นชุด chip ที่ต่างกันไปตาม tab (ส่งมาจากไฟล์ tab ที่เรียกใช้ — แต่ละ tab เจอ status
  // ได้ไม่เหมือนกันจริงตาม backend เช่น backlog ไม่มีทาง SUCCESS, archive มีแต่ SUCCESS อย่างเดียว)
  statusFilters = [],
  onStatusFilterToggle,
  statusOptions,
  paginationMode = 'page',
  onLoadMore,
  hasMore,
  highlightQuery,
  // ตัวระบุ tab ที่เรียกใช้ (backlog/future/archive/all/today/labor) — ยังไม่มี logic แยกตาม tabVariant
  // ในนี้เลยตอนนี้ (component เดียวกันทุก tab เหมือนเดิม) แค่ส่งผ่านไปเป็น data-attribute บน root ไว้
  // เป็น hook ให้ tab ไหนจะ override style เฉพาะตัวทีหลังได้ (เช่น query CSS จาก [data-tab-variant="archive"])
  // โดยไม่ต้อง fork component นี้ทั้งไฟล์
  tabVariant,
  // ปุ่ม "เรียงตาม" — ตัว component นี้แค่ render ปุ่ม/เมนูให้ ตัว state จริง (sortBy/sortDir) กับการ
  // ยิง fetch ใหม่เป็นหน้าที่ของไฟล์ tab ที่เรียกใช้ (เหมือน onMaxLookbackChange) เพราะแต่ละ tab ยิง
  // API ของตัวเอง component เดียวนี้ไม่รู้จักและไม่ควรผูกกับ fetch logic ของ tab ไหนโดยเฉพาะ
  sortBy = 'createdAt',
  sortDir = 'desc',
  onSortChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.project);
  const sentinelRef = useRef(null);
  const tableContainerRef = useRef(null);

  // ไฮไลท์คำค้นหาในข้อความที่แสดง (รายการ/รหัส/ผู้รับเงิน/ผู้ขอเบิก) ให้ตรงกับคำที่ใช้ค้นหาจริง
  const highlightText = (text) => {
    const str = text == null ? '' : String(text);
    const q = highlightQuery?.trim();
    if (!q) return str;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = str.split(new RegExp(`(${escaped})`, 'gi'));
    if (parts.length === 1) return str;
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#fff59d', padding: 0 }}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  // infinite scroll — โหลดหน้าถัดไปอัตโนมัติเมื่อ scroll ถึง sentinel ท้ายตาราง
  useEffect(() => {
    if (paginationMode !== 'infinite' || !onLoadMore) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !expenses?.isLoading) {
          onLoadMore();
        }
      },
      { root: tableContainerRef.current, rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [paginationMode, onLoadMore, hasMore, expenses?.isLoading]);
  const budgetState = useSelector((state) => state.budget);

  const [copiedId, setCopiedId] = useState(null);

  // ── ปุ่ม "เรียงตาม" ──────────────────────────────────────────────────────────
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  // แท็บ "archive" (Success) กับ "rejected" (ปฏิเสธ/ยกเลิก) มีแค่ status เดียว/กลุ่มเดียวอยู่แล้วในตัวเอง
  // (ถูกกรองด้วย tab match ฝั่ง backend มาก่อนแล้ว) ตัวเลือก "เรียงตามสถานะ" เลยไม่มีผลจริง (ทุกแถว priority
  // เท่ากันหมด) — ซ่อนตัวเลือกนี้ไปเฉพาะสองแท็บนี้ กันสับสนว่ากดแล้วทำไมลำดับไม่เปลี่ยน
  const SORT_OPTIONS = [
    { sortBy: 'createdAt', sortDir: 'desc', label: 'วันที่สร้าง: ล่าสุดก่อน' },
    { sortBy: 'createdAt', sortDir: 'asc', label: 'วันที่สร้าง: เก่าสุดก่อน' },
    { sortBy: 'date', sortDir: 'desc', label: 'วันที่รับเงิน: ล่าสุดก่อน' },
    { sortBy: 'date', sortDir: 'asc', label: 'วันที่รับเงิน: เก่าสุดก่อน' },
    ...(['archive', 'rejected'].includes(tabVariant)
      ? []
      : [
          {
            sortBy: 'status',
            sortDir: 'asc',
            label: 'สถานะ: รออนุมัติก่อน (Pending → Success)',
          },
          {
            sortBy: 'status',
            sortDir: 'desc',
            label: 'สถานะ: สำเร็จก่อน (Success → Pending)',
          },
        ]),
  ];
  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.sortBy === sortBy && o.sortDir === sortDir)
      ?.label || 'เรียงตาม';

  // ── Change Project/Budget modal ─────────────────────────────────────────────

  const [changeProjOpen, setChangeProjOpen] = useState(false);
  const [targetExpense, setTargetExpense] = useState(null);
  const [newProject, setNewProject] = useState(null);
  const [newBudget, setNewBudget] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Full Detail Modal ───────────────────────────────────────────────────────
  const [detailModalId, setDetailModalId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const openDetailModal = (id) => {
    setDetailModalId(id);
    setDetailModalOpen(true);
  };
  const closeDetailModal = () => {
    setDetailModalOpen(false);
    onDetailClose?.();
  };

  const [editModalId, setEditModalId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const openEditModal = (id) => {
    setEditModalId(id);
    setEditModalOpen(true);
  };
  const closeEditModal = () => {
    setEditModalOpen(false);
    onDetailClose?.();
  };

  const handleContextMenu = (event, expense) => {
    event.preventDefault();
    setTargetExpense(expense);
    setNewProject(expense?.project || null);
    setNewBudget(expense?.budget || null);
    setChangeProjOpen(true);
    dispatch(actions.projectAll({ lite: true }));
    if (expense?.project?._id) {
      dispatch(actions.budgetAll({ project_id: expense.project._id }));
    }
  };

  // เรียกเมื่อ user เปลี่ยน project เอง (ไม่ใช่ตอน initial load)
  const handleProjectChange = (_, val) => {
    setNewProject(val);
    setNewBudget(null);
    if (val?._id) dispatch(actions.budgetAll({ project_id: val._id }));
  };

  const handleSaveChangeProj = async () => {
    if (!targetExpense?._id) return;
    setSaving(true);
    try {
      await dispatch(
        actions.expensesPut(targetExpense._id, {
          project: newProject?._id || null,
          budget: newBudget?._id || null,
        }),
      );
    } finally {
      setSaving(false);
      setChangeProjOpen(false);
      setTargetExpense(null);
    }
  };

  const projectList = projects?.rows || [];
  const budgetList = budgetState?.rows || [];

  const STATUS_LABEL = {
    PENDING: 'รออนุมัติ (Pending)',
    PREAPPROVE: 'Pre-Approved',
    APPROVE_1: '1st Approved',
    APPROVE_2: '2nd Approved',
    APPROVE: 'อนุมัติแล้ว (Approved)',
    PREPARE: 'เตรียมจ่าย (Prepared)',
    SUCCESS: 'สำเร็จ (Success)',
    REJECT: 'ไม่อนุมัติ (Rejected)',
    HOLD: 'Hold',
    CANCEL: 'ยกเลิก (Cancelled)',
  };

  // รายการค่าแรง auto-sync (isLabor) ไม่มี employee ผูกอยู่เลย (สร้างจากระบบ ไม่ใช่พนักงานคนไหนกดขอเบิกเอง)
  const getApplicantLabel = (e) =>
    e?.isLabor
      ? 'ระบบอัติโนมัติ'
      : [e?.employee?.firstname, e?.employee?.lastname]
          .filter(Boolean)
          .join(' ') || '-';

  const handleCopyRow = (e) => {
    const type =
      e?.expenses_type === 'PAY'
        ? 'เบิกเพื่อจ่าย'
        : e?.expenses_type === 'REFUND'
        ? 'เบิกคืน'
        : '-';
    const project =
      [
        e?.project?.project_number,
        [e?.budget?.prefix, e?.budget?.budget_number, e?.budget?.name]
          .filter(Boolean)
          .join(''),
      ]
        .filter(Boolean)
        .join(' | ') || '-';
    const vatParts = [];
    if (e?.includes_vat) vatParts.push('VAT 7%');
    if (e?.withholding_tax > 0)
      vatParts.push(
        `หัก ณ ที่จ่าย${
          e?.withholding_tax_rate ? ` ${e.withholding_tax_rate}%` : ''
        }`,
      );
    const tax = vatParts.length > 0 ? vatParts.join(', ') : '-';
    const netPrice = (
      e?.net_price || (e?.price || 0) - (e?.withholding_tax || 0)
    )
      .toFixed(2)
      .replace(/\d(?=(\d{3})+(?!\d))/g, '$&,');
    const payeeParts = [
      e?.payee?.name,
      e?.payee?.bank,
      e?.payee?.account_number,
    ]
      .filter(Boolean)
      .join(' | ');
    const applicant = getApplicantLabel(e);
    const statusLabel = STATUS_LABEL[e?.status] || e?.status || '-';
    const text = [
      `ID: ${e?.code || '-'}`,
      `สถานะ: ${statusLabel}`,
      `วันที่ขอรับเงิน: ${dayjs(e?.date).format('DD/MM/YYYY')}`,
      `ประเภท: ${type}`,
      `โครงการ: ${project}`,
      `รายการ: ${e?.name || '-'}`,
      `ภาษี: ${tax}`,
      `ยอดจ่าย: ${netPrice} บาท`,
      `ผู้รับเงิน: ${payeeParts || '-'}`,
      `ผู้ขอเบิก: ${applicant}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(e._id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  // Get current user ID
  const currentUserId = me?.userData?._id;

  // Helper function to extract ID (handle both string IDs and objects with _id)
  const getIdValue = (approver) => {
    if (!approver) return null;
    return approver?._id || approver;
  };

  // Check if user is the designated approver at each level
  const canApprove1 =
    currentUserId && getIdValue(info?.expense_approver_1) === currentUserId;
  const canApprove2 =
    currentUserId && getIdValue(info?.expense_approver_2) === currentUserId;
  const canApprove3 =
    currentUserId && getIdValue(info?.expense_approver_3) === currentUserId;
  const canApproveAny = canApprove1 || canApprove2 || canApprove3;
  const userPerms = me?.userData?.permissions || {};
  const canSeeOverBudget =
    canApproveAny || !!(userPerms.admin || userPerms.finance_approve);

  const approvalMode = info?.expense_approval_mode || 'THREE_STEP';
  const isOneStep = approvalMode === 'ONE_STEP';

  const getStatusGroup = (status) => {
    if (['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'].includes(status))
      return 'pending';
    if (status === 'APPROVE') return 'approve';
    if (status === 'PREPARE') return 'prepare';
    if (status === 'SUCCESS') return 'success';
    if (status === 'LABOR_DRAFT') return 'labor';
    return 'other';
  };

  const groupConfig = {
    pending: {
      label: 'รออนุมัติ (Pending)',
      color: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.18),
    },
    labor: {
      label: 'รอบันทึกเงินเดือน (ยังไม่ตั้งเบิกจริง)',
      color: theme.palette.secondary.main,
      bg: alpha(theme.palette.secondary.main, 0.15),
    },
    approve: {
      label: 'อนุมัติแล้ว (Approved)',
      color: theme.palette.warning.dark,
      bg: alpha(theme.palette.warning.dark, 0.18),
    },
    prepare: {
      label: 'เตรียมจ่าย (Prepared)',
      color: theme.palette.info.main,
      bg: alpha(theme.palette.info.main, 0.18),
    },
    success: {
      label: 'สำเร็จ (Success)',
      color: theme.palette.success.main,
      bg: alpha(theme.palette.success.main, 0.18),
    },
    other: {
      label: 'อื่นๆ',
      color: theme.palette.grey[600],
      bg: alpha(theme.palette.grey[500], 0.15),
    },
  };

  const checkColor = (e) => {
    if (e?.status === 'PENDING') {
      return (
        <Chip
          size="small"
          label={isOneStep ? 'Pending' : 'Pending (1st)'}
          color="default"
        />
      );
    }
    if (e?.status === 'APPROVE_1')
      return <Chip size="small" label="1st Approved" color="secondary" />;
    if (e?.status === 'APPROVE_2')
      return <Chip size="small" label="2nd Approved" color="primary" />;
    if (e?.status === 'PREAPPROVE')
      return <Chip size="small" label="Pre-Approved" color="secondary" />;
    if (e?.status === 'APPROVE') {
      return (
        <Chip
          size="small"
          label={isOneStep ? 'Approved' : '3rd Approved'}
          color="warning"
        />
      );
    }
    if (e?.status === 'SUCCESS')
      return <Chip size="small" label="Success" color="success" />;
    if (e?.status === 'REJECT')
      return <Chip size="small" label="Rejected" color="error" />;
    if (e?.status === 'LABOR_DRAFT')
      return (
        <Chip
          size="small"
          label="รอบันทึกเงินเดือน"
          color="secondary"
          variant="outlined"
        />
      );
    if (e?.status === 'HOLD')
      return <Chip size="small" label="Hold" color="error" />;
    if (e?.status === 'PREPARE')
      return <Chip size="small" label="Prepared" color="info" />;
    if (e?.status === 'CANCEL') {
      return (
        <Chip
          size="small"
          label="Cancelled"
          sx={{ backgroundColor: 'black', color: 'white' }}
        />
      );
    }
  };

  // ปุ่ม "รีเซ็ต" — โผล่เฉพาะรายการค่าแรง auto-sync (isLabor) ที่ยัง PENDING และงวดยังไม่บันทึกเงินเดือน
  // (e.canReset คำนวณมาจากผู้เรียก เช่น ExpensesTabLabor.js เพราะต้องเช็คกับ salary-list/check-period แยกต่างหาก
  // ไม่ใช่ข้อมูลที่มากับตัว Expense เอง) แสดงคู่กับปุ่มอนุมัติ/ปฏิเสธปกติ ไม่ใช่แทนที่ — อนุมัติได้ตามปกติถ้ายอดถูกอยู่แล้ว
  const renderActionButtons = (e) => {
    const normalButtons = renderApprovalButtons(e);
    if (!e?.canReset) {
      return normalButtons;
    }
    return (
      <Stack
        direction={isMobile ? 'row' : 'column'}
        spacing={1}
        sx={{ width: '100%' }}
      >
        <Button
          variant="outlined"
          size="small"
          color="warning"
          onClick={() => onResetLabor && onResetLabor(e)}
          fullWidth={isMobile}
        >
          โหลดใหม่
        </Button>
        {normalButtons}
      </Stack>
    );
  };

  const renderApprovalButtons = (e) => {
    // ค่าแรง draft (isLabor, ยังไม่กดบันทึกเงินเดือน) — แก้ได้แค่ budget/payee เท่านั้น (บังคับที่ backend
    // อยู่แล้ว ดู expenses.service.js update()) เลยให้ปุ่มเปิด dialog เล็กแยกต่างหาก ไม่ใช้ EditExpenses ฟอร์มเต็ม
    if (e?.status === 'LABOR_DRAFT') {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEditLaborDraft && handleEditLaborDraft(e)}
          fullWidth={isMobile}
        >
          แก้ไข
        </Button>
      );
    }

    // One-step mode: simplified approve / reject at PENDING
    if (isOneStep && e?.status === 'PENDING') {
      if (!canApproveAny) {
        return (
          <Tooltip title="ไม่มีสิทธิ์อนุมัติ" placement="top">
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <p>ไม่มีสิทธิ์อนุมัติ</p>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => handleApprove(e._id)}
            fullWidth={isMobile}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleReject(e._id)}
            fullWidth={isMobile}
          >
            Reject
          </Button>
        </Stack>
      );
    }

    // Three-step mode: original multi-step behaviour
    if (!isOneStep && e?.status === 'PENDING') {
      // Show 1st Approve button only if user is the designated approver_1
      if (!canApprove1) {
        return (
          <Tooltip
            title="ต้องเป็นผู้อนุมัติระดับ 1 ตามที่ระบุในระบบ"
            placement="top"
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <p>ไม่มีสิทธิ์อนุมัติ</p>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => handleApprove1(e._id)}
            fullWidth={isMobile}
          >
            1st Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleReject1(e._id)}
            fullWidth={isMobile}
          >
            Reject
          </Button>
        </Stack>
      );
    }
    if (!isOneStep && e?.status === 'APPROVE_1') {
      // Show 2nd Approve button only if user is the designated approver_2
      if (!canApprove2) {
        return (
          <Tooltip
            title="ต้องเป็นผู้อนุมัติระดับ 2 ตามที่ระบุในระบบ"
            placement="top"
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <p>ไม่มีสิทธิ์อนุมัติ</p>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => handleApprove2(e._id)}
            fullWidth={isMobile}
          >
            2nd Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleReject2(e._id)}
            fullWidth={isMobile}
          >
            Reject
          </Button>
        </Stack>
      );
    }
    if (!isOneStep && e?.status === 'APPROVE_2') {
      // Show 3rd Approve button only if user is the designated approver_3
      if (!canApprove3) {
        return (
          <Tooltip
            title="ต้องเป็นผู้อนุมัติระดับ 3 ตามที่ระบุในระบบ"
            placement="top"
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <p>ไม่มีสิทธิ์อนุมัติ</p>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => handleApprove3(e._id)}
            fullWidth={isMobile}
          >
            3rd Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleReject3(e._id)}
            fullWidth={isMobile}
          >
            Reject
          </Button>
        </Stack>
      );
    }
    if (e?.status === 'APPROVE') {
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => handleOpenPay(e)}
            fullWidth={isMobile}
          >
            Add Payment
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => handleReset(e._id)}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
        </Stack>
      );
    }
    if (e?.status === 'PREAPPROVE') {
      if (isOneStep) {
        if (!canApproveAny) {
          return (
            <Tooltip title="ไม่มีสิทธิ์อนุมัติ" placement="top">
              <Box
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                }}
              >
                <p>ไม่มีสิทธิ์อนุมัติ</p>
              </Box>
            </Tooltip>
          );
        }
        return (
          <Stack
            direction={isMobile ? 'row' : 'column'}
            spacing={1}
            sx={{ width: '100%' }}
          >
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={() => handleApprove(e._id)}
              fullWidth={isMobile}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => handleReject(e._id)}
              fullWidth={isMobile}
            >
              Reject
            </Button>
          </Stack>
        );
      }
      if (!canApprove1) {
        return (
          <Tooltip
            title="ต้องเป็นผู้อนุมัติระดับ 1 ตามที่ระบุในระบบ"
            placement="top"
          >
            <Box
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <p>ไม่มีสิทธิ์อนุมัติ</p>
            </Box>
          </Tooltip>
        );
      }
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => handleApprove1(e._id)}
            fullWidth={isMobile}
          >
            1st Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleReject1(e._id)}
            fullWidth={isMobile}
          >
            Reject
          </Button>
        </Stack>
      );
    }
    if (e?.status === 'REJECT') {
      return (
        <Stack
          direction={isMobile ? 'row' : 'column'}
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => openEditModal(e._id)}
            fullWidth={isMobile}
          >
            แก้ไข
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => handleReset(e._id)}
            fullWidth={isMobile}
          >
            RESET
          </Button>
        </Stack>
      );
    }
    if (e?.status === 'HOLD') {
      return (
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={() => handleReset(e._id)}
          fullWidth={isMobile}
        >
          RESET
        </Button>
      );
    }
    if (e?.status === 'CANCEL') {
      return (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleReset(e._id)}
          fullWidth={isMobile}
        >
          RESET
        </Button>
      );
    }
  };

  // sentinel ที่ IntersectionObserver เฝ้าดู (ดู useEffect ด้านบน) ต้องเป็น descendant จริงของ
  // scroll container ที่ระบุเป็น root (tableContainerRef) ไม่งั้น intersection ไม่มีวันเป็น true เลย
  // (บั๊กเดิม: sentinel เคย render เป็น sibling อยู่นอก TableContainer ทำให้ infinite scroll ค้างที่หน้าแรก
  // เสมอบนจอ desktop) — render อันเดียวกันนี้ทั้งฝั่ง mobile (ท้าย card list, root เป็น viewport เพราะไม่มี
  // tableContainerRef ผูกในโหมดนั้น) และฝั่ง desktop (ต้องอยู่ในสุดของ TableContainer)
  const renderSentinel = () =>
    paginationMode === 'infinite' && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
        }}
        ref={sentinelRef}
      >
        {expenses?.isLoading && !_.isEmpty(filter) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              กำลังโหลดเพิ่ม...
            </Typography>
          </Stack>
        )}
        {!hasMore && !_.isEmpty(filter) && (
          <Typography variant="caption" color="text.secondary">
            — แสดงครบทุกรายการแล้ว ({expenses?.total || 0}) —
          </Typography>
        )}
      </Box>
    );

  return (
    <div data-tab-variant={tabVariant}>
      <Paper>
        <div className="p-2 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {onSortChange && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<SwapVertIcon fontSize="small" />}
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {currentSortLabel}
                </Button>
                <Menu
                  anchorEl={sortMenuAnchor}
                  open={!!sortMenuAnchor}
                  onClose={() => setSortMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: '14px', mt: 0.5 } }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem
                      key={`${opt.sortBy}-${opt.sortDir}`}
                      dense
                      selected={
                        opt.sortBy === sortBy && opt.sortDir === sortDir
                      }
                      onClick={() => {
                        onSortChange(opt.sortBy, opt.sortDir);
                        setSortMenuAnchor(null);
                      }}
                    >
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </div>
          <div className="flex flex-row items-center gap-2 flex-wrap justify-end">
            {onStatusFilterToggle &&
              statusOptions?.map(({ key, label, color }) => {
                const active =
                  key === 'ALL'
                    ? statusFilters.length === 0
                    : statusFilters.includes(key);
                return (
                  <Chip
                    key={key}
                    label={label}
                    color={active ? color : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    onClick={() => onStatusFilterToggle(key)}
                    size="small"
                    sx={{ fontWeight: active ? 700 : 400 }}
                  />
                );
              })}
            {/* <div className="flex-1 sm:flex-initial">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleOpenPrepareModal()}
                fullWidth
              >
                Prepare
              </Button>
            </div> */}
          </div>
        </div>

        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {expenses?.isLoading && _.isEmpty(filter) ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <Card key={i}>
                    <CardContent>
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="60%" height={16} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </CardContent>
                  </Card>
                ))
            ) : _.isEmpty(filter) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <p>ไม่มีข้อมูล</p>
              </Box>
            ) : (
              filter.map((e, index) => {
                // จัดกลุ่ม/ไล่สีตาม status เฉพาะแท็บ "วันนี้" เท่านั้น (เดิมทำทุกแท็บ) — แท็บอื่นเรียบๆ ไม่มีสี
                const group = tabVariant === 'today' ? getStatusGroup(e.status) : null;
                const prevGroup =
                  tabVariant === 'today' && index > 0 ? getStatusGroup(filter[index - 1].status) : null;
                const gc = group ? groupConfig[group] : null;
                return (
                  <React.Fragment key={e._id}>
                    {gc && group !== prevGroup && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: gc.bg,
                          borderLeft: `4px solid ${gc.color}`,
                          borderRadius: 1,
                          mt: index === 0 ? 0 : 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{
                            color: gc.color,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {gc.label}
                        </Typography>
                      </Box>
                    )}
                    <Card
                      sx={{
                        position: 'relative',
                        ...(gc && { borderLeft: `3px solid ${gc.color}`, bgcolor: alpha(gc.color, 0.07) }),
                      }}
                    >
                      <CardContent>
                        {/* No. */}
                        <Box
                          sx={{
                            mt: 4,
                            fontWeight: 'bold',
                            color: '#999',
                            fontSize: '0.75rem',
                          }}
                        >
                          #{index + 1}
                        </Box>

                        {/* Date Created & Status */}
                        <Box
                          sx={{
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <div className="text-xs text-gray-500">
                              วันที่สร้าง
                            </div>
                            <div className="font-semibold text-sm">
                              {dayjs(e?.createdAt).format('DD/MM/YYYY')}
                            </div>
                          </Box>
                          <Box>{checkColor(e)}</Box>
                        </Box>

                        {/* Code & Request Date */}
                        <Box sx={{ mb: 2, borderTop: '1px solid #eee', pt: 1 }}>
                          <div className="text-xs text-gray-500">เอกสาร</div>
                          <div className="font-bold text-theme-600 text-sm mb-1">
                            {highlightText(e?.code)}
                          </div>
                          <div className="text-xs text-gray-500">วันที่ขอ</div>
                          <div className="text-sm">
                            {dayjs(e?.date).format('DD/MM/YYYY')}
                          </div>
                        </Box>

                        {/* Type */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">ประเภท</div>
                          {e?.expenses_type === 'PAY' ? (
                            <Chip
                              size="small"
                              label="เบิกเพื่อจ่าย"
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          ) : e?.expenses_type === 'REFUND' ? (
                            <Chip
                              size="small"
                              label="เบิกคืน"
                              color="secondary"
                              sx={{ mt: 0.5 }}
                            />
                          ) : (
                            <Chip size="small" label="-" sx={{ mt: 0.5 }} />
                          )}
                        </Box>

                        {/* Project & Budget */}
                        <Box
                          sx={{ mb: 2 }}
                          onDoubleClick={
                            e?.project
                              ? (ev) => {
                                  ev.stopPropagation();
                                  onDoubleClickProject?.(
                                    e.project._id || e.project.id,
                                  );
                                }
                              : undefined
                          }
                        >
                          <div className="text-xs text-gray-500">โครงการ</div>
                          <div className="font-bold text-sm">
                            {e?.project?.project_number || '-'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {e?.budget?.prefix}
                            {e?.budget?.budget_number} {e?.budget?.name || '-'}
                          </div>
                          {canSeeOverBudget &&
                            e?.budget?.cost > 0 &&
                            (e?.budget?.total_expenses ?? 0) >
                              e?.budget?.cost && (
                              <Tooltip
                                title={`งบหมวดนี้เกินแล้ว: ใช้ไป ${(
                                  e.budget.total_expenses ?? 0
                                ).toLocaleString('th-TH', {
                                  maximumFractionDigits: 0,
                                })} / ตั้ง ${e.budget.cost.toLocaleString(
                                  'th-TH',
                                  { maximumFractionDigits: 0 },
                                )} บาท`}
                              >
                                <span
                                  style={{
                                    color: '#d97706',
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    cursor: 'default',
                                  }}
                                >
                                  ⚠ งบหมวดนี้เกินแล้ว
                                </span>
                              </Tooltip>
                            )}
                        </Box>

                        {/* Order */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">รายการ</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span
                              className="text-sm"
                              onDoubleClick={(ev) => {
                                ev.stopPropagation();
                                openDetailModal(e._id);
                              }}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                              {highlightText(e?.name)}
                            </span>
                            <Tooltip title="ดูรายละเอียดรายการเบิกจ่าย">
                              <IconButton
                                size="small"
                                onClick={() => openDetailModal(e._id)}
                                sx={{ p: 0 }}
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </Box>

                        {/* Tax */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">ภาษี</div>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ mt: 0.5, flexWrap: 'wrap' }}
                          >
                            {e?.includes_vat && (
                              <Chip
                                size="small"
                                label="VAT 7%"
                                color="success"
                                variant="outlined"
                              />
                            )}
                            {e?.withholding_tax > 0 && (
                              <Chip
                                size="small"
                                label="หัก ณ ที่จ่าย"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                            {!e?.includes_vat && !(e?.withholding_tax > 0) && (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </Stack>
                        </Box>

                        {/* Amount - ยอดจ่ายจริงเท่านั้น */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">
                            ยอดจ่ายจริง
                          </div>
                          <div className="font-bold text-lg text-green-600">
                            ฿
                            {(
                              e?.net_price ||
                              (e?.price || 0) - (e?.withholding_tax || 0)
                            )
                              .toFixed(0)
                              .replace(/\d(?=(\d{3})+(?!\d))/g, '$&,')}
                          </div>
                        </Box>

                        {/* Payee */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">ผู้รับ</div>
                          <div className="text-sm">
                            <p className="mb-1">
                              <b>ชื่อ:</b>{' '}
                              {e?.payee?.name
                                ? highlightText(e.payee.name)
                                : '-'}
                            </p>
                            <p className="mb-1">
                              <b>ธนาคาร:</b> {e?.payee?.bank || '-'}
                            </p>
                            <p>
                              <b>บัญชี:</b> {e?.payee?.account_number || '-'}
                            </p>
                          </div>
                        </Box>

                        {/* Applicant */}
                        <Box sx={{ mb: 2 }}>
                          <div className="text-xs text-gray-500">ผู้ขอ</div>
                          <div className="text-sm font-semibold">
                            {e?.isLabor ? (
                              'ระบบอัติโนมัติ'
                            ) : (
                              <>
                                {highlightText(e?.employee?.firstname)}{' '}
                                {highlightText(e?.employee?.lastname)}
                              </>
                            )}
                          </div>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ borderTop: '1px solid #eee', pt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color={copiedId === e._id ? 'success' : 'inherit'}
                            startIcon={
                              copiedId === e._id ? (
                                <CheckIcon />
                              ) : (
                                <ContentCopyIcon />
                              )
                            }
                            onClick={() => handleCopyRow(e)}
                            fullWidth
                            sx={{ mb: 1 }}
                          >
                            {copiedId === e._id ? 'คัดลอกแล้ว!' : 'คัดลอก'}
                          </Button>
                          {renderActionButtons(e)}
                        </Box>
                      </CardContent>
                    </Card>
                  </React.Fragment>
                );
              })
            )}
            {renderSentinel()}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            sx={
              paginationMode === 'infinite'
                ? { maxHeight: 1000, overflowY: 'auto' }
                : undefined
            }
          >
            <Table
              size="small"
              stickyHeader={paginationMode === 'infinite'}
              sx={
                paginationMode === 'infinite'
                  ? {
                      '& .MuiTableCell-stickyHeader': {
                        backgroundColor: '#4338ca',
                      },
                    }
                  : undefined
              }
            >
              <TableHead>
                <TableRow className="bg-theme-600">
                  <TableCell align="center">
                    <h1 className="text-white">คัดลอก</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">No.</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">Date Created</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">ID / Request Date</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">Type</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">Project</h1>
                  </TableCell>

                  <TableCell align="center">
                    <h1 className="text-white">Order</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">ภาษี</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">ยอดจ่ายจริง</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">Payee</h1>
                  </TableCell>

                  <TableCell align="center">
                    <h1 className="text-white">Applicant</h1>
                  </TableCell>
                  <TableCell align="center">
                    <h1 className="text-white">Actions</h1>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses?.isLoading && _.isEmpty(filter) ? (
                  Array(8)
                    .fill(0)
                    .map((_, i) => (
                      <TableRow key={`skel-${i}`}>
                        {Array(12)
                          .fill(0)
                          .map((__, j) => (
                            <TableCell key={j}>
                              <Skeleton
                                variant="text"
                                width={j === 0 ? 24 : j === 11 ? 80 : '70%'}
                              />
                            </TableCell>
                          ))}
                      </TableRow>
                    ))
                ) : _.isEmpty(filter) ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No items
                    </TableCell>
                  </TableRow>
                ) : (
                  filter.map((e, index) => {
                    // จัดกลุ่ม/ไล่สีตาม status เฉพาะแท็บ "วันนี้" เท่านั้น (เดิมทำทุกแท็บ) — แท็บอื่นเรียบๆ ไม่มีสี
                    const group = tabVariant === 'today' ? getStatusGroup(e.status) : null;
                    const prevGroup =
                      tabVariant === 'today' && index > 0
                        ? getStatusGroup(filter[index - 1].status)
                        : null;
                    const gc = group ? groupConfig[group] : null;
                    return (
                      <React.Fragment key={e._id}>
                        {gc && group !== prevGroup && (
                          <TableRow>
                            <TableCell
                              colSpan={12}
                              sx={{
                                py: 0.75,
                                px: 2,
                                bgcolor: gc.bg,
                                borderLeft: `4px solid ${gc.color}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                sx={{
                                  color: gc.color,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                }}
                              >
                                {gc.label}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          onDoubleClick={() =>
                            onRowDoubleClick && onRowDoubleClick(e._id)
                          }
                          sx={{
                            cursor: onRowDoubleClick ? 'pointer' : 'default',
                            ...(gc && { bgcolor: alpha(gc.color, 0.07) }),
                          }}
                        >
                          <TableCell align="center">
                            <Tooltip
                              title={
                                copiedId === e._id
                                  ? 'คัดลอกแล้ว!'
                                  : 'คัดลอกข้อมูลแถวนี้'
                              }
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                color={
                                  copiedId === e._id ? 'success' : 'inherit'
                                }
                                startIcon={
                                  copiedId === e._id ? (
                                    <CheckIcon />
                                  ) : (
                                    <ContentCopyIcon />
                                  )
                                }
                                onClick={() => handleCopyRow(e)}
                                sx={{
                                  fontSize: '0.7rem',
                                  px: 1,
                                  py: 0.3,
                                  minWidth: 0,
                                }}
                              >
                                คัดลอก
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell align="center">
                            <h1>{dayjs(e?.createdAt).format('DD/MM/YYYY')}</h1>
                            <h1>{checkColor(e)}</h1>
                          </TableCell>
                          <TableCell align="center">
                            <h1 className="font-bold text-theme-600">
                              {highlightText(e?.code)}
                            </h1>
                            <h1>{dayjs(e?.date).format('DD/MM/YYYY')}</h1>
                          </TableCell>
                          <TableCell align="center">
                            {e?.expenses_type === 'PAY' ? (
                              <Chip
                                size="small"
                                label="เบิกเพื่อจ่าย"
                                color="primary"
                              />
                            ) : e?.expenses_type === 'REFUND' ? (
                              <Chip
                                size="small"
                                label="เบิกคืน"
                                color="secondary"
                              />
                            ) : (
                              <Chip size="small" label="-" />
                            )}
                          </TableCell>
                          <TableCell
                            onClick={
                              e?.project
                                ? (ev) => {
                                    ev.stopPropagation();
                                    onDoubleClickProject?.(
                                      e.project._id || e.project.id,
                                    );
                                  }
                                : undefined
                            }
                            onContextMenu={(ev) => handleContextMenu(ev, e)}
                            sx={{
                              cursor: e?.project ? 'pointer' : 'default',
                              userSelect: 'none',
                              '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' },
                            }}
                          >
                            <h1 className="font-bold">
                              {e?.project?.project_number || '-'}
                            </h1>
                            <h1 className="font-bold">
                              {e?.budget?.prefix}
                              {e?.budget?.budget_number}{' '}
                              {e?.budget?.name || '-'}
                            </h1>
                            {canSeeOverBudget &&
                              e?.budget?.cost > 0 &&
                              (e?.budget?.total_expenses ?? 0) >
                                e?.budget?.cost && (
                                <Tooltip
                                  title={`ใช้ไป ${(
                                    e.budget.total_expenses ?? 0
                                  ).toLocaleString('th-TH', {
                                    maximumFractionDigits: 0,
                                  })} / ตั้ง ${e.budget.cost.toLocaleString(
                                    'th-TH',
                                    { maximumFractionDigits: 0 },
                                  )} บาท`}
                                >
                                  <span
                                    style={{
                                      color: '#d97706',
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      cursor: 'default',
                                    }}
                                  >
                                    ⚠ งบหมวดนี้เกินแล้ว
                                  </span>
                                </Tooltip>
                              )}
                          </TableCell>

                          <TableCell>
                            <div className="flex items-center">
                              <h1
                                onDoubleClick={(ev) => {
                                  ev.stopPropagation();
                                  openDetailModal(e._id);
                                }}
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                {highlightText(e?.name)}
                              </h1>
                              <Tooltip title="ดูรายละเอียดรายการเบิกจ่าย">
                                <IconButton
                                  size="small"
                                  onClick={() => openDetailModal(e._id)}
                                >
                                  <LaunchIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            <Stack spacing={0.5} alignItems="center">
                              {e?.includes_vat && (
                                <Chip
                                  size="small"
                                  label="VAT 7%"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                              {e?.withholding_tax > 0 && (
                                <Chip
                                  size="small"
                                  label={`หัก ณ ที่จ่าย${
                                    e?.withholding_tax_rate
                                      ? ` ${e.withholding_tax_rate}%`
                                      : ''
                                  }`}
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {!e?.includes_vat &&
                                !(e?.withholding_tax > 0) && (
                                  <span className="text-gray-400">-</span>
                                )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            ฿
                            {(
                              e?.net_price ||
                              (e?.price || 0) - (e?.withholding_tax || 0)
                            )
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+(?!\d))/g, '$&,')}
                          </TableCell>
                          <TableCell>
                            <h1>
                              <b>Name: </b>
                              {e?.payee?.name
                                ? highlightText(e.payee.name)
                                : '-'}
                            </h1>
                            <h1>
                              <b>Bank: </b>
                              {e?.payee?.bank || '-'}
                            </h1>
                            <h1>
                              <b>Account: </b>
                              {e?.payee?.account_number || '-'}
                            </h1>
                          </TableCell>

                          <TableCell align="center">
                            {e?.isLabor ? (
                              'ระบบอัติโนมัติ'
                            ) : (
                              <>
                                {highlightText(e?.employee?.firstname)}{' '}
                                {highlightText(e?.employee?.lastname)}
                              </>
                            )}
                          </TableCell>
                          <TableCell>{renderActionButtons(e)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {renderSentinel()}
          </TableContainer>
        )}

        {paginationMode !== 'infinite' && (
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 200]}
            component="div"
            count={expenses?.total || 0}
            rowsPerPage={size}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* ── Full Expense Detail Modal ── */}
      <Modal
        open={detailModalOpen}
        onClose={closeDetailModal}
        closeAfterTransition
      >
        <Fade in={detailModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                รายละเอียดรายการเบิกจ่าย
              </Typography>
              <IconButton
                size="small"
                onClick={closeDetailModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {detailModalId && (
                <ExpensesDetail
                  propId={detailModalId}
                  onClose={closeDetailModal}
                />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* ── Edit Expense Modal ── */}
      <Modal open={editModalOpen} onClose={closeEditModal} closeAfterTransition>
        <Fade in={editModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'warning.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                แก้ไขรายการเบิกจ่าย
              </Typography>
              <IconButton
                size="small"
                onClick={closeEditModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {editModalId && (
                <EditExpenses propId={editModalId} onClose={closeEditModal} />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* ── Change Project/Budget Dialog ── */}
      <Dialog
        open={changeProjOpen}
        onClose={() => setChangeProjOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>เปลี่ยน Project / Budget</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              รายการ: <b>{targetExpense?.code}</b> — {targetExpense?.name}
            </Typography>
            <Autocomplete
              options={projectList}
              value={newProject}
              onChange={handleProjectChange}
              getOptionLabel={(o) =>
                `${o.project_number || ''} ${o.name || ''}`.trim()
              }
              isOptionEqualToValue={(o, v) => o._id === v._id}
              loading={projects?.isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {projects?.isLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={budgetList}
              value={newBudget}
              onChange={(_, val) => setNewBudget(val)}
              disabled={!newProject}
              getOptionLabel={(o) =>
                `${o.prefix || ''}${o.budget_number || ''} ${
                  o.name || ''
                }`.trim()
              }
              isOptionEqualToValue={(o, v) => o._id === v._id}
              loading={budgetState?.isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Budget"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {budgetState?.isLoading && (
                          <CircularProgress size={16} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeProjOpen(false)} disabled={saving}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChangeProj}
            disabled={saving || !newProject}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
